import type { RequestContext } from "../context/request-context.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { PermissionCode } from "../../domain/identity/access-types.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import type { JsonObject } from "../../shared/kernel/json.js";
import { hashPassword, verifyPassword } from "../../domain/identity/password-hash.js";
import { computeDashboard, type DashboardFilters, type DashboardUnitArea } from "../../domain/dashboard/dashboard-indicators.js";
import {
  buildInspectorRanking,
  rdoFiltersFor,
  toDashboardAssignments,
  toDashboardFieldProgress,
  toDashboardInspections
} from "../../domain/dashboard/dashboard-inputs.js";
import {
  DASHBOARD_SHARE_TYPE,
  SHARE_AUDIT_EVENT,
  SHARE_MESSAGES,
  SHARE_REFRESH_INTERVALS,
  SHARE_STATE_LABELS,
  clampFiltersToScope,
  effectiveRefreshSeconds,
  expiresAtFor,
  generateShareToken,
  isWellFormedShareToken,
  opaqueKey,
  sanitizeForShare,
  sharePath,
  shareLinkState,
  validateShareInput,
  type CreateShareLinkInput,
  type DashboardShareEvent,
  type DashboardShareEventType,
  type DashboardShareLink,
  type DashboardShareScope,
  type ShareAccess,
  type ShareLinkState,
  type ShareRefreshInterval,
  type ShareRefreshMode,
  type ShareScopeMode,
  type ShareUniverse,
  type ShareValidity,
  MIN_SHARE_PASSWORD_LENGTH
} from "../../domain/dashboard/dashboard-share.js";
import type { DashboardIndicators } from "../../domain/dashboard/dashboard-indicators.js";
import type { InspectorPerformanceReport } from "../../domain/dashboard/inspector-performance.js";
import type { DashboardShareRepository } from "../../repositories/dashboard-share-repository.js";

const randomUUID = () => globalThis.crypto.randomUUID();

/**
 * As ÚNICAS permissões que o contexto de leitura do link carrega, e sempre
 * em INTERSEÇÃO com as que o autor tem hoje. Nenhuma de escrita: mesmo que
 * algum caminho de leitura tentasse gravar, o contexto não autorizaria.
 * `identity.read` só entra quando o link mostra o ranking por Inspetor.
 */
export const SHARE_READ_PERMISSIONS: readonly PermissionCode[] = [
  PermissionCode.EquipmentRead,
  PermissionCode.InspectionRead,
  PermissionCode.ReportRead,
  PermissionCode.RecommendationRead,
  PermissionCode.FieldControlViewAll,
  PermissionCode.ProjectRead,
  PermissionCode.RdoRead
];

/** Os serviços de leitura do Painel — os mesmos que a tela interna chama. */
export interface DashboardDataSources {
  equipmentService?: { listEquipments(context: RequestContext, filter?: { projectId?: string | null }): Promise<readonly any[]> };
  inspectionService?: { listAllInspections(context: RequestContext, options?: { projectId?: string | null }): Promise<readonly any[]> };
  reportService?: { listReports(context: RequestContext, options?: { projectId?: string | null }): Promise<readonly any[]>; listAllRecommendations(context: RequestContext, options?: { projectId?: string | null }): Promise<readonly any[]> };
  fieldControlService?: { listScope(context: RequestContext, filter: any): Promise<any>; listAssignments(context: RequestContext, filter: any): Promise<readonly any[]> };
  rdoService?: { listRdos(context: RequestContext, filters: any): Promise<readonly any[]> };
  projectService?: { listProjects(clientId?: string, status?: any, context?: RequestContext): Promise<readonly any[]>; listUnitAreas(filter?: any, clientId?: string, context?: RequestContext): Promise<readonly any[]> };
  adminService?: { listProfessionals(context: RequestContext): Promise<readonly any[]> };
}

/** O autor do link, como está HOJE no cadastro. */
export interface ShareOwner {
  readonly active: boolean;
  readonly permissions: ReadonlySet<string>;
  readonly professionalId: string | null;
  readonly name: string | null;
}

export interface DashboardShareDeps {
  readonly repo: DashboardShareRepository;
  readonly sources: DashboardDataSources;
  readonly audit?: AuditRecorder | undefined;
  readonly owner: (userId: string) => Promise<ShareOwner | null>;
  /** O usuário ainda tem acesso ao Cliente (§37.1)? */
  readonly clientAccess: (userId: string, permissions: ReadonlySet<string>, clientId: string) => Promise<boolean>;
  readonly clientName?: (clientId: string) => Promise<string | null>;
  readonly now?: () => Date;
}

// ═══════════════════════════════════════════════════════════ estado de acesso

/**
 * Liberações de senha e limite de tentativas. Em memória do processo: o
 * servidor é um processo só, e reiniciá-lo apenas pede a senha de novo —
 * nunca libera nada.
 */
export class ShareAccessGuard {
  private readonly grants = new Map<string, { linkId: string; expiresAt: number }>();
  private readonly failures = new Map<string, { count: number; windowStart: number; lockedUntil: number }>();
  private readonly lastTouch = new Map<string, number>();
  static readonly GRANT_TTL_MS = 12 * 3_600_000;
  static readonly MAX_FAILURES = 8;
  static readonly WINDOW_MS = 15 * 60_000;
  static readonly LOCK_MS = 15 * 60_000;

  issueGrant(linkId: string, now: number, linkExpiresAt: string | null): string {
    const grant = generateShareToken();
    const cap = linkExpiresAt ? Date.parse(linkExpiresAt) : Infinity;
    this.grants.set(grant, { linkId, expiresAt: Math.min(now + ShareAccessGuard.GRANT_TTL_MS, cap) });
    return grant;
  }

  hasGrant(grant: unknown, linkId: string, now: number): boolean {
    if (!isWellFormedShareToken(grant)) return false;
    const g = this.grants.get(grant);
    if (!g) return false;
    if (g.expiresAt <= now) { this.grants.delete(grant); return false; }
    return g.linkId === linkId;
  }

  /** Senha trocada, token renovado ou link revogado: toda liberação daquele link cai. */
  dropGrants(linkId: string): void {
    for (const [grant, g] of this.grants) if (g.linkId === linkId) this.grants.delete(grant);
  }

  lockedFor(linkId: string, now: number): number {
    const f = this.failures.get(linkId);
    return f && f.lockedUntil > now ? Math.ceil((f.lockedUntil - now) / 1000) : 0;
  }

  recordFailure(linkId: string, now: number): void {
    const f = this.failures.get(linkId);
    const fresh = !f || now - f.windowStart > ShareAccessGuard.WINDOW_MS;
    const count = fresh ? 1 : f.count + 1;
    this.failures.set(linkId, {
      count, windowStart: fresh ? now : f.windowStart,
      lockedUntil: count >= ShareAccessGuard.MAX_FAILURES ? now + ShareAccessGuard.LOCK_MS : 0
    });
  }

  clearFailures(linkId: string): void {
    this.failures.delete(linkId);
  }

  /** Grava último acesso no máximo uma vez por minuto por link (o polling não vira escrita contínua). */
  shouldTouch(linkId: string, now: number, force: boolean): boolean {
    const last = this.lastTouch.get(linkId) ?? 0;
    if (!force && now - last < 60_000) return false;
    this.lastTouch.set(linkId, now);
    return true;
  }
}

// ═══════════════════════════════════════════════════════════ tipos de tela

export interface ShareScopeLabels {
  mode: ShareScopeMode;
  showInspectors: boolean;
  project: string;
  unitArea: string;
  inspector: string;
  period: string;
}

export interface DashboardShareLinkView {
  id: string;
  name: string;
  description: string | null;
  /** Caminho relativo; a tela monta a URL com a própria origem. */
  path: string;
  state: ShareLinkState;
  stateLabel: string;
  scope: ShareScopeLabels;
  refreshMode: ShareRefreshMode;
  refreshIntervalSeconds: ShareRefreshInterval;
  access: ShareAccess;
  expiresAt: string | null;
  revokedAt: string | null;
  revokeReason: string | null;
  createdBy: string;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  lastAccessAt: string | null;
  accessCount: number;
  canEdit: boolean;
  canRevoke: boolean;
}

export interface UpdateShareLinkInput {
  name?: string;
  description?: string | null;
  refreshMode?: ShareRefreshMode;
  refreshIntervalSeconds?: number;
  access?: ShareAccess;
  /** Obrigatória ao passar para PASSWORD; opcional para trocar a senha atual. */
  password?: string | null;
}

export interface RenewShareLinkInput {
  validity: ShareValidity;
  customExpiresAt?: string | null;
  /** Gera token novo: o link antigo para de abrir. */
  rotateToken?: boolean;
}

/** Filtros do visitante — por chave opaca, nunca por id. */
export interface SharedFilterRequest {
  projectKey?: string | null;
  unitAreaKey?: string | null;
  inspectorKey?: string | null;
  from?: string | null;
  to?: string | null;
}

export interface SharedOpenRequest {
  password?: string | null;
  grant?: string | null;
  filters?: SharedFilterRequest | null;
  /** Primeira abertura da página (registra ACCESSED); o polling manda false. */
  initial?: boolean;
}

export interface SharedOpenMeta {
  ip?: string | null;
  userAgent?: string | null;
}

export interface SharedOption { key: string; label: string; projectKey?: string | null }

export type SharedDashboardResult =
  | {
      status: "OK";
      link: {
        name: string;
        description: string | null;
        clientName: string | null;
        mode: ShareScopeMode;
        refreshMode: ShareRefreshMode;
        refreshSeconds: number;
        expiresAt: string | null;
        showInspectors: boolean;
        scope: ShareScopeLabels;
      };
      options: {
        projects: SharedOption[];
        unitAreas: SharedOption[];
        inspectors: SharedOption[];
        period: { min: string | null; max: string | null };
        locked: { project: boolean; unitArea: boolean; inspector: boolean; period: boolean };
      };
      applied: SharedFilterRequest;
      data: DashboardIndicators;
      performance: InspectorPerformanceReport | null;
      /** Por que o ranking não pôde ser montado (vínculos ilegíveis), quando for o caso. */
      rankingNote: string | null;
      generatedAt: string;
      grant?: string;
    }
  | { status: "EXPIRED" | "REVOKED" | "NOT_FOUND" | "UNAVAILABLE" | "PASSWORD_REQUIRED" | "PASSWORD_INVALID" | "LOCKED"; message: string; retryAfterSeconds?: number };

// ═══════════════════════════════════════════════════════════ núcleo comum

const fmtDay = (day: string | null): string => {
  if (!day) return "";
  const [y, m, d] = day.split("-");
  return y && m && d ? `${d}/${m}/${y}` : day;
};

function periodLabel(from: string | null, to: string | null): string {
  if (from && to) return `${fmtDay(from)} a ${fmtDay(to)}`;
  if (from) return `a partir de ${fmtDay(from)}`;
  if (to) return `até ${fmtDay(to)}`;
  return "Todo o período";
}

abstract class DashboardShareCore {
  protected constructor(protected readonly deps: DashboardShareDeps, protected readonly guard: ShareAccessGuard) {}

  protected now(): Date {
    return this.deps.now ? this.deps.now() : new Date();
  }

  protected async event(link: DashboardShareLink, event: DashboardShareEventType, userId: string, userName: string | null, detail: Record<string, unknown>): Promise<void> {
    const at = this.now().toISOString();
    await this.deps.repo.appendEvent({ id: randomUUID(), linkId: link.id, event, at, userId, userName, detail });
    // Auditoria oficial, em melhor esforço: o audit_logs exige Cliente e
    // usuário espelhado, e a trilha que nunca falha é a tabela própria acima.
    // O token NUNCA entra: quem lesse a auditoria abriria o painel.
    const action = event === "CREATED" ? AuditAction.Create : event === "REVOKED" ? AuditAction.Inactivate : AuditAction.Update;
    await this.deps.audit?.record(
      { activeClientId: link.clientId as any, correlationId: randomUUID() as any, userId: (userId === "public" ? link.createdBy : userId) as any, professionalId: null, permissions: new Set() },
      { action, entityType: "DashboardShareLink", entityId: link.id, previousState: null, newState: null, metadata: { event: SHARE_AUDIT_EVENT[event], actor: userId, ...detail } as unknown as JsonObject }
    ).catch(() => null);
  }

  /** Nomes para a tela — nunca ids crus. */
  protected async scopeLabels(scope: DashboardShareScope, context: RequestContext | null): Promise<ShareScopeLabels> {
    const src = this.deps.sources;
    let project = "Todos os Projetos";
    let unitArea = "Todas as Unidades / Áreas";
    let inspector = "Todos os Inspetores";
    try {
      if (scope.projectId) {
        const p = (await src.projectService?.listProjects(scope.clientId, undefined, context ?? undefined) ?? []).find((x: any) => String(x.id) === scope.projectId);
        project = p ? [p.code, p.name].filter(Boolean).join(" - ") : "Projeto não encontrado";
      }
      if (scope.unitAreaId) {
        const u = (await src.projectService?.listUnitAreas({ clientId: scope.clientId }, undefined, context ?? undefined) ?? []).find((x: any) => String(x.id) === scope.unitAreaId);
        unitArea = u ? [u.code, u.name].filter(Boolean).join(" - ") : "Unidade não encontrada";
      }
      if (scope.inspectorProfessionalId) {
        if (!scope.showInspectors) inspector = "Recorte por Inspetor (nome não compartilhado)";
        else {
          const people = context ? await src.adminService?.listProfessionals(context).catch(() => []) ?? [] : [];
          const person = people.find((x: any) => String(x.id) === scope.inspectorProfessionalId);
          inspector = person ? String(person.name) : "Inspetor selecionado";
        }
      }
    } catch { /* rótulo é conveniência: nunca derruba a operação */ }
    return { mode: scope.mode, showInspectors: scope.showInspectors, project, unitArea, inspector, period: periodLabel(scope.from, scope.to) };
  }
}

// ═══════════════════════════════════════════════════════════ fachada autenticada (RPC)

/**
 * Fachada autenticada — a ÚNICA que entra no `/api/rpc`. Toda operação confere
 * a permissão do ato. O acesso público pelo token é outra classe
 * (`DashboardSharePublicService`), fora do RPC: o `/api/rpc` expõe todos os
 * métodos de um serviço liberado.
 */
export class DashboardShareService extends DashboardShareCore {
  constructor(deps: DashboardShareDeps, guard: ShareAccessGuard) {
    super(deps, guard);
  }

  private has(context: RequestContext, code: PermissionCode): boolean {
    return context.permissions.has(code);
  }

  private require(context: RequestContext, ...codes: PermissionCode[]): void {
    if (!codes.some(c => this.has(context, c))) {
      throw new AppError(ErrorCode.Authorization, "Dashboard share permission missing", { safeMessage: "Você não tem permissão para links compartilhados do Painel." });
    }
  }

  /**
   * "manage" alcança os links do Cliente ativo (de qualquer Cliente, para quem
   * administra o sistema). Sem ela, cada um mexe só nos próprios.
   */
  private canTouch(context: RequestContext, link: DashboardShareLink, own: PermissionCode): boolean {
    if (this.has(context, PermissionCode.DashboardShareManage)
      && (this.has(context, PermissionCode.AdministrationManage) || link.clientId === String(context.activeClientId ?? ""))) return true;
    return link.createdBy === String(context.userId) && this.has(context, own);
  }

  private async load(context: RequestContext, id: string, own: PermissionCode): Promise<DashboardShareLink> {
    const link = await this.deps.repo.findById(String(id ?? ""));
    // Link fora do alcance responde como inexistente: não confirma que o id existe.
    if (!link || !this.canTouch(context, link, own)) throw new AppError(ErrorCode.NotFound, "Share link not found", { safeMessage: "Link não encontrado." });
    return link;
  }

  private async userName(context: RequestContext): Promise<string | null> {
    return (await this.deps.owner(String(context.userId)).catch(() => null))?.name ?? null;
  }

  private async view(context: RequestContext, link: DashboardShareLink): Promise<DashboardShareLinkView> {
    const state = shareLinkState(link, this.now());
    return {
      id: link.id, name: link.name, description: link.description, path: sharePath(link.token),
      state, stateLabel: SHARE_STATE_LABELS[state], scope: await this.scopeLabels(link.scope, context),
      refreshMode: link.refreshMode, refreshIntervalSeconds: link.refreshIntervalSeconds, access: link.access,
      expiresAt: link.expiresAt, revokedAt: link.revokedAt, revokeReason: link.revokeReason,
      createdBy: link.createdBy, createdByName: link.createdByName, createdAt: link.createdAt, updatedAt: link.updatedAt,
      lastAccessAt: link.lastAccessAt, accessCount: link.accessCount,
      canEdit: state !== "REVOKED" && this.canTouch(context, link, PermissionCode.DashboardShareCreate),
      canRevoke: state !== "REVOKED" && this.canTouch(context, link, PermissionCode.DashboardShareRevoke)
    };
  }

  async listLinks(context: RequestContext): Promise<DashboardShareLinkView[]> {
    this.require(context, PermissionCode.DashboardShareRead, PermissionCode.DashboardShareManage);
    const clientId = context.activeClientId ? String(context.activeClientId) : undefined;
    const manage = this.has(context, PermissionCode.DashboardShareManage);
    const links = manage && clientId
      ? await this.deps.repo.list({ clientId })
      : manage && this.has(context, PermissionCode.AdministrationManage)
      ? await this.deps.repo.list({})
      : await this.deps.repo.list({ createdBy: String(context.userId) });
    return Promise.all(links.map(l => this.view(context, l)));
  }

  async createLink(context: RequestContext, input: CreateShareLinkInput): Promise<DashboardShareLinkView> {
    this.require(context, PermissionCode.DashboardShareCreate);
    const clientId = await this.clientOfScope(context, input.scope?.projectId || null);
    if (!(await this.deps.clientAccess(String(context.userId), context.permissions, clientId).catch(() => false))) {
      throw new AppError(ErrorCode.Authorization, "No client access", { safeMessage: "Você não possui autorização para este cliente." });
    }
    const errors = validateShareInput(input);
    const now = this.now();
    const validity = expiresAtFor(input.validity, now, input.customExpiresAt);
    if (validity.error) errors.push(validity.error);
    const scope = await this.checkedScope(context, clientId, input.scope, errors);
    if (errors.length > 0) throw new AppError(ErrorCode.Validation, "Invalid share link", { safeMessage: errors.join(" ") });

    const at = now.toISOString();
    const userName = await this.userName(context);
    const link: DashboardShareLink = {
      id: randomUUID(), token: generateShareToken(), clientId,
      name: input.name.trim(), description: input.description?.trim() || null, dashboardType: DASHBOARD_SHARE_TYPE,
      scope, refreshMode: input.refreshMode,
      refreshIntervalSeconds: (input.refreshMode === "INTERVAL" ? Number(input.refreshIntervalSeconds) : 15) as ShareRefreshInterval,
      access: input.access, passwordHash: input.access === "PASSWORD" ? await hashPassword(String(input.password)) : null,
      expiresAt: validity.expiresAt, revokedAt: null, revokedBy: null, revokeReason: null,
      createdBy: String(context.userId), createdByName: userName, createdAt: at, updatedAt: at, updatedBy: String(context.userId),
      lastAccessAt: null, accessCount: 0
    };
    await this.deps.repo.insert(link);
    await this.event(link, "CREATED", String(context.userId), userName, {
      scope: { ...scope }, expiresAt: link.expiresAt, access: link.access, refreshMode: link.refreshMode
    });
    return this.view(context, link);
  }

  /**
   * O Cliente do link sai do PROJETO escolhido no escopo (decisão do usuário,
   * 2026-09-27: "a seleção do projeto já sinaliza o cliente"). Sem Projeto
   * (escopo "todos os projetos"), vale o Cliente ativo da sessão. Os dados do
   * Painel são sempre de um Cliente — todo repositório filtra por ele —, por
   * isso um dos dois precisa existir. O acesso do autor a esse Cliente é
   * conferido logo depois, como para qualquer documento (§37.1).
   */
  private async clientOfScope(context: RequestContext, projectId: string | null): Promise<string> {
    if (projectId) {
      const project = (await this.deps.sources.projectService?.listProjects(undefined, undefined, context).catch(() => []) ?? []).find((p: any) => String(p.id) === projectId);
      if (!project) throw new AppError(ErrorCode.Validation, "Project not found", { safeMessage: "O Projeto escolhido não foi encontrado." });
      const clientId = String(project.clientId ?? "");
      if (!clientId) throw new AppError(ErrorCode.Validation, "Project without client", { safeMessage: "O Projeto escolhido não tem Cliente cadastrado." });
      return clientId;
    }
    const active = context.activeClientId ? String(context.activeClientId) : "";
    if (!active) throw new AppError(ErrorCode.Validation, "Scope client required", { safeMessage: "Escolha o Projeto do painel — ele identifica o Cliente dos dados compartilhados." });
    return active;
  }

  /** O escopo pedido precisa existir no Cliente — nada de id inventado gravado no link. */
  private async checkedScope(context: RequestContext, clientId: string, s: CreateShareLinkInput["scope"], errors: string[]): Promise<DashboardShareScope> {
    const scope: DashboardShareScope = {
      clientId,
      projectId: s?.projectId || null,
      unitAreaId: s?.unitAreaId || null,
      inspectorProfessionalId: s?.inspectorProfessionalId || null,
      from: s?.from || null,
      to: s?.to || null,
      mode: s?.mode === "DYNAMIC" ? "DYNAMIC" : "FIXED",
      showInspectors: Boolean(s?.showInspectors)
    };
    const src = this.deps.sources;
    if (scope.projectId) {
      const projects = await src.projectService?.listProjects(clientId, undefined, context).catch(() => []) ?? [];
      if (!projects.some((p: any) => String(p.id) === scope.projectId)) errors.push("O Projeto escolhido não pertence ao Cliente ativo.");
    }
    if (scope.unitAreaId) {
      const units = await src.projectService?.listUnitAreas({ clientId }, undefined, context).catch(() => []) ?? [];
      const unit = units.find((u: any) => String(u.id) === scope.unitAreaId);
      if (!unit) errors.push("A Unidade / Área escolhida não pertence ao Cliente ativo.");
      else if (scope.projectId && String(unit.projectId ?? "") !== scope.projectId) errors.push("A Unidade / Área escolhida não pertence ao Projeto do escopo.");
    }
    if (scope.showInspectors && !this.has(context, PermissionCode.IdentityRead)) {
      errors.push("Seu perfil não lê o cadastro de Profissionais: o ranking por Inspetor não pode ser compartilhado.");
    }
    if (scope.inspectorProfessionalId && this.has(context, PermissionCode.IdentityRead)) {
      const people = await src.adminService?.listProfessionals(context).catch(() => []) ?? [];
      if (!people.some((p: any) => String(p.id) === scope.inspectorProfessionalId)) errors.push("O Inspetor escolhido não foi encontrado.");
    }
    return scope;
  }

  async updateLink(context: RequestContext, id: string, patch: UpdateShareLinkInput): Promise<DashboardShareLinkView> {
    this.require(context, PermissionCode.DashboardShareCreate, PermissionCode.DashboardShareManage);
    const link = await this.load(context, id, PermissionCode.DashboardShareCreate);
    if (link.revokedAt) throw new AppError(ErrorCode.Validation, "Revoked", { safeMessage: "Link revogado não pode ser alterado. Crie um novo link." });
    const errors: string[] = [];
    const name = patch.name !== undefined ? String(patch.name).trim() : link.name;
    if (name.length < 3 || name.length > 120) errors.push("O nome do link deve ter de 3 a 120 caracteres.");
    const description = patch.description !== undefined ? (String(patch.description ?? "").trim() || null) : link.description;
    if (description && description.length > 500) errors.push("A descrição pode ter no máximo 500 caracteres.");
    const refreshMode = patch.refreshMode ?? link.refreshMode;
    if (!["REALTIME", "INTERVAL", "MANUAL"].includes(refreshMode)) errors.push("Modo de atualização inválido.");
    let interval = link.refreshIntervalSeconds;
    if (refreshMode === "INTERVAL") {
      const asked = patch.refreshIntervalSeconds !== undefined ? Number(patch.refreshIntervalSeconds) : interval;
      if (!SHARE_REFRESH_INTERVALS.includes(asked as ShareRefreshInterval)) errors.push("O intervalo de atualização deve ser 15, 30 ou 60 segundos.");
      else interval = asked as ShareRefreshInterval;
    }
    const access = patch.access ?? link.access;
    let passwordHash = link.passwordHash;
    let passwordChanged = false;
    if (access === "PUBLIC") { passwordChanged = link.access !== "PUBLIC"; passwordHash = null; }
    else if (patch.password !== undefined && patch.password !== null && patch.password !== "") {
      if (String(patch.password).length < MIN_SHARE_PASSWORD_LENGTH) errors.push(`A senha do link precisa ter pelo menos ${MIN_SHARE_PASSWORD_LENGTH} caracteres.`);
      else { passwordHash = await hashPassword(String(patch.password)); passwordChanged = true; }
    } else if (link.access !== "PASSWORD") errors.push("Informe a senha para proteger o link.");
    if (errors.length > 0) throw new AppError(ErrorCode.Validation, "Invalid share link update", { safeMessage: errors.join(" ") });

    const next: DashboardShareLink = { ...link, name, description, refreshMode, refreshIntervalSeconds: interval, access, passwordHash, updatedAt: this.now().toISOString(), updatedBy: String(context.userId) };
    await this.save(next, link.updatedAt);
    if (passwordChanged) this.guard.dropGrants(link.id);
    const changed = (["name", "description", "refreshMode", "refreshIntervalSeconds", "access"] as const).filter(k => next[k] !== link[k]);
    await this.event(next, "UPDATED", String(context.userId), await this.userName(context), { changed: passwordChanged ? [...changed, "password"] : changed });
    return this.view(context, next);
  }

  async renewLink(context: RequestContext, id: string, input: RenewShareLinkInput): Promise<DashboardShareLinkView> {
    this.require(context, PermissionCode.DashboardShareCreate, PermissionCode.DashboardShareManage);
    const link = await this.load(context, id, PermissionCode.DashboardShareCreate);
    if (link.revokedAt) throw new AppError(ErrorCode.Validation, "Revoked", { safeMessage: "Link revogado não pode ser renovado — a revogação é definitiva. Crie um novo link." });
    const validity = expiresAtFor(input?.validity, this.now(), input?.customExpiresAt);
    if (validity.error) throw new AppError(ErrorCode.Validation, "Invalid validity", { safeMessage: validity.error });
    const rotate = Boolean(input?.rotateToken);
    const next: DashboardShareLink = {
      ...link, expiresAt: validity.expiresAt, token: rotate ? generateShareToken() : link.token,
      updatedAt: this.now().toISOString(), updatedBy: String(context.userId)
    };
    await this.save(next, link.updatedAt);
    if (rotate) this.guard.dropGrants(link.id);
    await this.event(next, "RENEWED", String(context.userId), await this.userName(context), { previousExpiresAt: link.expiresAt, expiresAt: next.expiresAt, tokenRotated: rotate });
    return this.view(context, next);
  }

  async revokeLink(context: RequestContext, id: string, reason?: string | null): Promise<DashboardShareLinkView> {
    this.require(context, PermissionCode.DashboardShareRevoke, PermissionCode.DashboardShareManage);
    const link = await this.load(context, id, PermissionCode.DashboardShareRevoke);
    if (link.revokedAt) return this.view(context, link);
    const at = this.now().toISOString();
    const next: DashboardShareLink = { ...link, revokedAt: at, revokedBy: String(context.userId), revokeReason: reason ? String(reason).trim().slice(0, 300) || null : null, updatedAt: at, updatedBy: String(context.userId) };
    await this.save(next, link.updatedAt);
    this.guard.dropGrants(link.id);
    await this.event(next, "REVOKED", String(context.userId), await this.userName(context), { reason: next.revokeReason });
    return this.view(context, next);
  }

  async listLinkEvents(context: RequestContext, id: string): Promise<DashboardShareEvent[]> {
    this.require(context, PermissionCode.DashboardShareRead, PermissionCode.DashboardShareManage);
    const link = await this.load(context, id, PermissionCode.DashboardShareRead);
    return this.deps.repo.listEvents(link.id, 100);
  }

  private async save(next: DashboardShareLink, expectedUpdatedAt: string): Promise<void> {
    if (!(await this.deps.repo.updateIfUnchanged(next, expectedUpdatedAt))) {
      throw new AppError(ErrorCode.Conflict, "Share link changed", { safeMessage: "O link foi alterado por outra pessoa. Recarregue a lista e tente de novo." });
    }
  }
}

// ═══════════════════════════════════════════════════════════ acesso público (fora do RPC)

/**
 * Abertura do Painel pelo TOKEN, sem sessão. Nunca entra no `/api/rpc`: é
 * chamada pelo endpoint público próprio (`/api/public/dashboard-share`) no
 * servidor e direto pela página no modo navegador.
 *
 * Nada do que o visitante manda é confiado: o token é conferido no banco, o
 * estado (expirado/revogado) é recalculado agora, o autor precisa continuar
 * ativo e com acesso ao Cliente, e os filtros passam por
 * `clampFiltersToScope`. Os dados são lidos com as permissões do AUTOR em
 * interseção com `SHARE_READ_PERMISSIONS` e devolvidos já calculados e
 * sanitizados.
 */
export class DashboardSharePublicService extends DashboardShareCore {
  constructor(deps: DashboardShareDeps, guard: ShareAccessGuard) {
    super(deps, guard);
  }

  async open(token: string, request: SharedOpenRequest = {}, meta: SharedOpenMeta = {}): Promise<SharedDashboardResult> {
    if (!isWellFormedShareToken(token)) return { status: "NOT_FOUND", message: SHARE_MESSAGES.NOT_FOUND };
    const link = await this.deps.repo.findByToken(token);
    if (!link) return { status: "NOT_FOUND", message: SHARE_MESSAGES.NOT_FOUND };
    const nowDate = this.now();
    const now = nowDate.getTime();
    const state = shareLinkState(link, nowDate);
    if (state === "REVOKED") return { status: "REVOKED", message: SHARE_MESSAGES.REVOKED };
    if (state === "EXPIRED") return { status: "EXPIRED", message: SHARE_MESSAGES.EXPIRED };

    let grant: string | undefined;
    if (link.access === "PASSWORD") {
      if (!this.guard.hasGrant(request.grant, link.id, now)) {
        const locked = this.guard.lockedFor(link.id, now);
        if (locked > 0) return { status: "LOCKED", message: SHARE_MESSAGES.LOCKED, retryAfterSeconds: locked };
        if (!request.password) return { status: "PASSWORD_REQUIRED", message: SHARE_MESSAGES.PASSWORD_REQUIRED };
        if (!(await verifyPassword(String(request.password), link.passwordHash))) {
          this.guard.recordFailure(link.id, now);
          const lockedNow = this.guard.lockedFor(link.id, now);
          return lockedNow > 0
            ? { status: "LOCKED", message: SHARE_MESSAGES.LOCKED, retryAfterSeconds: lockedNow }
            : { status: "PASSWORD_INVALID", message: SHARE_MESSAGES.PASSWORD_INVALID };
        }
        this.guard.clearFailures(link.id);
        grant = this.guard.issueGrant(link.id, now, link.expiresAt);
      }
    }

    const owner = await this.deps.owner(link.createdBy).catch(() => null);
    const ownerCanShare = !!owner && owner.active && (owner.permissions.has(PermissionCode.DashboardShareCreate) || owner.permissions.has(PermissionCode.DashboardShareManage));
    if (!owner || !ownerCanShare || !(await this.deps.clientAccess(link.createdBy, owner.permissions, link.clientId).catch(() => false))) {
      return { status: "UNAVAILABLE", message: SHARE_MESSAGES.UNAVAILABLE };
    }

    const allowed = new Set<string>(SHARE_READ_PERMISSIONS);
    if (link.scope.showInspectors) allowed.add(PermissionCode.IdentityRead);
    const context: RequestContext = {
      activeClientId: link.clientId as any,
      correlationId: randomUUID() as any,
      userId: link.createdBy as any,
      professionalId: (owner.professionalId as any) ?? null,
      permissions: new Set([...owner.permissions].filter(p => allowed.has(p))) as ReadonlySet<PermissionCode>
    };

    const payload = await this.compute(link, context, request.filters ?? null);

    if (this.guard.shouldTouch(link.id, now, Boolean(request.initial))) {
      await this.deps.repo.touchAccess(link.id, nowDate.toISOString(), Boolean(request.initial)).catch(() => undefined);
    }
    if (request.initial) {
      await this.event(link, "ACCESSED", "public", null, {
        ip: meta.ip ? String(meta.ip).slice(0, 64) : null,
        userAgent: meta.userAgent ? String(meta.userAgent).slice(0, 200) : null
      }).catch(() => undefined);
    }
    return grant ? { ...payload, grant } : payload;
  }

  private async compute(link: DashboardShareLink, ctx: RequestContext, requested: SharedFilterRequest | null): Promise<Extract<SharedDashboardResult, { status: "OK" }>> {
    const src = this.deps.sources;
    const scope = link.scope;
    const settle = async <T>(run: (() => Promise<T>) | undefined, fallback: T): Promise<{ ok: boolean; value: T }> => {
      if (!run) return { ok: false, value: fallback };
      try { return { ok: true, value: (await run()) ?? fallback }; } catch { return { ok: false, value: fallback }; }
    };

    const [projects, units, people] = await Promise.all([
      settle(src.projectService ? () => src.projectService!.listProjects(link.clientId, undefined, ctx) : undefined, [] as readonly any[]),
      settle(src.projectService ? () => src.projectService!.listUnitAreas({ clientId: link.clientId }, undefined, ctx) : undefined, [] as readonly any[]),
      scope.showInspectors ? settle(src.adminService ? () => src.adminService!.listProfessionals(ctx) : undefined, [] as readonly any[]) : Promise.resolve({ ok: true, value: [] as readonly any[] })
    ]);
    const label = (x: any) => [x.code, x.name].filter(Boolean).join(" - ") || "—";
    const projectList = projects.value.map((p: any) => ({ id: String(p.id), label: label(p) }));
    const unitList = units.value.map((u: any) => ({ id: String(u.id), label: label(u), projectId: String(u.projectId ?? "") }));
    const peopleList = people.value.filter((p: any) => !p.deletedAt).map((p: any) => ({ id: String(p.id), label: String(p.name || "—"), type: String(p.professionalType || "") }));

    const keys = new Map<string, string>();
    const keyFor = async (kind: "project" | "unit" | "inspector" | "system", id: string): Promise<string> => {
      const k = `${kind}:${id}`;
      let v = keys.get(k);
      if (!v) { v = await opaqueKey(link.id, kind, id); keys.set(k, v); }
      return v;
    };
    const byKey = async <T extends { id: string }>(kind: "project" | "unit" | "inspector", list: readonly T[], key: string | null | undefined): Promise<string | null> => {
      if (!key) return null;
      for (const item of list) if ((await keyFor(kind, item.id)) === key) return item.id;
      return null;
    };

    const universe: ShareUniverse = {
      projectIds: new Set(projectList.map(p => p.id)),
      unitAreas: new Map(unitList.map(u => [u.id, u.projectId])),
      inspectorIds: new Set(peopleList.map(p => p.id))
    };
    const asked: DashboardFilters = {
      projectId: await byKey("project", projectList, requested?.projectKey),
      unitAreaId: await byKey("unit", unitList, requested?.unitAreaKey),
      inspectorProfessionalId: await byKey("inspector", peopleList, requested?.inspectorKey),
      from: requested?.from ?? null,
      to: requested?.to ?? null
    };
    const applied = clampFiltersToScope(scope, asked, universe);

    // [ONLINE-TRACE][DASHBOARD] Diagnóstico temporário seguro — captura filtros
    // resolvidos e contexto autorizado para identificar divergência produção × teste.
    console.log("[ONLINE-TRACE][DASHBOARD]", JSON.stringify({
      userId: ctx.userId,
      activeClientId: (ctx as any).activeClientId ?? null,
      requestedProjectId: asked.projectId ?? null,
      requestedUnitAreaId: asked.unitAreaId ?? null,
      requestedInspectorId: asked.inspectorProfessionalId ?? null,
      dateFrom: asked.from ?? null,
      dateTo: asked.to ?? null,
      appliedProjectId: applied.projectId ?? null,
      appliedUnitAreaId: applied.unitAreaId ?? null,
      appliedInspectorId: applied.inspectorProfessionalId ?? null,
      scopeProjectId: scope.projectId ?? null,
      scopeClientId: scope.clientId ?? null
    }));

    // PROPAGAÇÃO DO PROJECTID NO PAINEL COMPARTILHADO: o escopo do link
    // (scope.projectId) é repassado a todos os services de leitura para
    // garantir isolamento real entre projetos, igual ao painel interno.
    const projectFilter = applied.projectId ? { projectId: applied.projectId } : undefined;
    const [equipments, inspections, reports, recommendations, fieldScope, assignments, rdo] = await Promise.all([
      settle(src.equipmentService ? () => src.equipmentService!.listEquipments(ctx, projectFilter) : undefined, [] as readonly any[]),
      settle(src.inspectionService ? () => src.inspectionService!.listAllInspections(ctx, projectFilter) : undefined, [] as readonly any[]),
      settle(src.reportService ? () => src.reportService!.listReports(ctx, projectFilter) : undefined, [] as readonly any[]),
      settle(src.reportService ? () => src.reportService!.listAllRecommendations(ctx, projectFilter) : undefined, [] as readonly any[]),
      settle(src.fieldControlService && ctx.permissions.has(PermissionCode.FieldControlViewAll) ? () => src.fieldControlService!.listScope(ctx, {}) : undefined, null as any),
      settle(src.fieldControlService && (ctx.permissions.has(PermissionCode.FieldControlViewAll) || ctx.permissions.has(PermissionCode.FieldControlViewOwn) || ctx.permissions.has(PermissionCode.FieldControlManageAssignments)) ? () => src.fieldControlService!.listAssignments(ctx, {}) : undefined, [] as readonly any[]),
      settle(src.rdoService ? () => src.rdoService!.listRdos(ctx, rdoFiltersFor(applied)) : undefined, [] as readonly any[])
    ]);

    const unitAreas: DashboardUnitArea[] = unitList.map(u => ({ id: u.id, code: "", name: u.label, projectId: u.projectId }));
    const fieldProgress = toDashboardFieldProgress(fieldScope.value?.rows);
    const mappedInspections = toDashboardInspections(inspections.value as any[]);
    const mappedAssignments = toDashboardAssignments(assignments.value);
    const data = computeDashboard({
      equipments: equipments.value as any[], inspections: mappedInspections, reports: reports.value as any[], recommendations: recommendations.value as any[],
      unitAreas, fieldProgress, assignments: mappedAssignments
    }, applied);
    const performance = scope.showInspectors
      ? buildInspectorRanking({ people: peopleList, inspections: mappedInspections, assignments: mappedAssignments, fieldProgress, systems: fieldScope.value?.systems, rdoEntries: rdo.value as any[], applied })
      : null;

    // Chaves opacas de tudo o que vai para a tela, calculadas antes da sanitização (que é síncrona).
    for (const b of data.unitProgress.bars) await keyFor("unit", b.id);
    for (const b of data.systemProgress.bars) await keyFor("system", b.id);
    for (const p of performance?.inspectors ?? []) {
      await keyFor("inspector", p.professionalId);
      for (const s of p.systems) await keyFor("system", s.systemId);
    }
    const safe = sanitizeForShare(data, performance, (kind, id) => keys.get(`${kind}:${id}`) ?? "—", scope.showInspectors);

    const dynamic = scope.mode === "DYNAMIC";
    const lockedProject = !dynamic || !!scope.projectId;
    const lockedUnit = !dynamic || !!scope.unitAreaId;
    const lockedInspector = !dynamic || !!scope.inspectorProfessionalId || !scope.showInspectors;
    const effectiveProject = applied.projectId ?? null;
    const projectOptions = await Promise.all(projectList
      .filter(p => (lockedProject ? p.id === scope.projectId : true))
      .map(async p => ({ key: await keyFor("project", p.id), label: p.label })));
    const unitOptions = await Promise.all(unitList
      .filter(u => (lockedUnit ? u.id === scope.unitAreaId : (!effectiveProject || u.projectId === effectiveProject)))
      .map(async u => ({ key: await keyFor("unit", u.id), label: u.label, projectKey: u.projectId ? await keyFor("project", u.projectId) : null })));
    const inspectorOptions = scope.showInspectors
      ? await Promise.all(peopleList
          .filter(p => (lockedInspector ? p.id === scope.inspectorProfessionalId : (p.type === "INSPECTOR" || p.type === "NDT_INSPECTOR" || (performance?.inspectors ?? []).some(x => x.professionalId === p.id))))
          .map(async p => ({ key: await keyFor("inspector", p.id), label: p.label })))
      : [];

    return {
      status: "OK",
      link: {
        name: link.name, description: link.description,
        clientName: this.deps.clientName ? await this.deps.clientName(link.clientId).catch(() => null) : null,
        mode: scope.mode, refreshMode: link.refreshMode, refreshSeconds: effectiveRefreshSeconds(link), expiresAt: link.expiresAt,
        showInspectors: scope.showInspectors, scope: await this.scopeLabels(scope, ctx)
      },
      options: {
        projects: projectOptions.sort((a, b) => a.label.localeCompare(b.label, "pt-BR")),
        unitAreas: unitOptions.sort((a, b) => a.label.localeCompare(b.label, "pt-BR")),
        inspectors: inspectorOptions.sort((a, b) => a.label.localeCompare(b.label, "pt-BR")),
        period: { min: scope.from, max: scope.to },
        locked: { project: lockedProject, unitArea: lockedUnit, inspector: lockedInspector, period: !dynamic }
      },
      applied: {
        projectKey: applied.projectId ? await keyFor("project", applied.projectId) : null,
        unitAreaKey: applied.unitAreaId ? await keyFor("unit", applied.unitAreaId) : null,
        inspectorKey: applied.inspectorProfessionalId && scope.showInspectors ? await keyFor("inspector", applied.inspectorProfessionalId) : null,
        from: applied.from ?? null,
        to: applied.to ?? null
      },
      data: safe.data,
      performance: safe.performance,
      rankingNote: scope.showInspectors && !assignments.ok ? "Os vínculos de Inspetor não puderam ser lidos: o ranking mostra só o que vem das inspeções." : null,
      generatedAt: this.now().toISOString()
    };
  }
}
